import type {DesignerStores} from '@packages/systems/designer/flux';
import {hasResourceAccess} from './checkSiteAuthorizations';

type LocaleAccessState = Pick<
  DesignerStores,
  'AuthorizationsStore' | 'LocalizationStore' | 'SiteDataStore'
>;

/**
 * Checks whether the user has editContent access on ALL site locales.
 * Returns true (permissive) when authorization data isn't loaded,
 * no locale restrictions are configured, or the site has no locales.
 */
export const selectHasUnrestrictedLocaleAccess = (
  state: LocaleAccessState
): boolean => {
  const {AuthorizationsStore, SiteDataStore, LocalizationStore} = state;

  if (!AuthorizationsStore?.siteAuthorizations?.hasLoaded) return true;

  const userId = SiteDataStore?.userId;
  if (!userId) return true;

  const userAuthData = AuthorizationsStore.siteAuthorizations?.data?.[userId];
  if (!userAuthData?.SiteLocale) return true;

  const primaryId = LocalizationStore?.primaryLocale?._id;
  const secondaryIds = (LocalizationStore?.locales ?? []).map((l) => l._id);
  const localeIds = primaryId ? [primaryId, ...secondaryIds] : secondaryIds;
  if (localeIds.length === 0) return true;

  return localeIds.every((id) =>
    hasResourceAccess(userAuthData, 'SiteLocale', 'editContent', id)
  );
};
